<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('POSTALCODES')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.countryCode"
                    :items="countriesFilterItems"
                    :loading="countriesFilter.isLoading"
                    :search-input.sync="countriesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('postalCodes.country')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="countriesFilter.isEdit = true"
                    @change="countriesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-text-field
                    v-model="filter.agencyCode"
                    :label="$t('zones.agency')"
                    v-if="!$userIsAdmin()"
                    disabled
                  ></v-text-field>
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    v-if="$userIsAdmin()"
                    :disabled="!$userIsAdmin()"
                    :items="agenciesFilterItems"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('postalCodes.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-autocomplete
                    v-model="filter.zoneCode"
                    :items="zonesFilterItems"
                    :loading="zonesFilter.isLoading"
                    :search-input.sync="zonesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('postalCodes.zone')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="zonesFilter.isEdit = true"
                    @change="zonesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="12" md="4" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/postalCodes"
              :filter="this.filter"
              :filterExport="filterExport"
              itemsPerPage="10000"
            />
            <ImportExcel
              url="/postalCodes"
              importMethod="/rangeBulk"
              @done="search"
              maxItems="10000"
              v-if="$checkPermission('POSTALCODES')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          item-key="code"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('POSTALCODES')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code, item.country.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('POSTALCODES')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code, item.country.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('POSTALCODES')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code, item.country.code)"
                  ></crud-button>
                </td>
                <td>{{ item.code }}</td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.country.code }}</span>
                    </template>
                    <span>{{ item.country.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agency.code }}</span>
                    </template>
                    <span>{{ item.agency.name }}</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.zone.code }}</span>
                    </template>
                    <span>{{ item.zone.name }}</span>
                  </v-tooltip>
                </td>
                <td>{{ item.trafficTime }}</td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('postalCodes.code')"
                    rules="required|max:12"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('postalCodes.code')"
                      v-model="item.code"
                      :disabled="editMode"
                      required
                      :error-messages="errors"
                      counter="12"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('postalCodes.country')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.countryCode"
                      :items="countriesItems"
                      :loading="countries.isLoading"
                      :search-input.sync="countries.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('postalCodes.country')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="countries.isEdit = true"
                      @change="
                        countries.isEdit = false;
                        resetAgenciesSearch();
                        resetZonesSearch();
                      "
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('postalCodes.country')"
                    v-model="item.countryString"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('postalCodes.agency')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="item.agencyCode"
                      :label="$t('zones.agency')"
                      v-if="!$userIsAdmin()"
                      disabled
                    ></v-text-field>
                    <v-autocomplete
                      v-model="item.agencyCode"
                      v-if="$userIsAdmin()"
                      :items="agenciesItems"
                      :loading="agencies.isLoading"
                      :search-input.sync="agencies.search"
                      hide-no-data
                      :disabled="isCountrySelected"
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('postalCodes.agency')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="agencies.isEdit = true"
                      @change="
                        agencies.isEdit = false;
                        resetZonesSearch();
                      "
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('postalCodes.zone')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.zoneCode"
                      :items="zonesItems"
                      :loading="zones.isLoading"
                      :search-input.sync="zones.search"
                      hide-no-data
                      :disabled="isAgencySelected"
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('postalCodes.zone')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      @focus="zones.isEdit = true"
                      @change="zones.isEdit = false"
                      :error-messages="errors"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('postalCodes.time')"
                    rules="required|max:10"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('postalCodes.time')"
                      v-model="item.trafficTime"
                      required
                      :error-messages="errors"
                      @keypress="integerOnlyField($event)"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import { integerOnlyField } from "@/js/utils.js";

const CONTEXT = "/postalCodes";
const CONTEXT_COUNTRIES = "/countries";
const CONTEXT_AGENCIES = "/agencies";
const CONTEXT_ZONES = "/zones";
const ENTITY_NAME = "postalCode.";

export default {
  components: {
    CrudButton,
    ExportExcel,
    ImportExcel
  },
  name: "PostalCode",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    dialog: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      countryCode: "",
      agencyCode: "",
      zoneCode: "",
      deletedItems: "ALL"
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    countries: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    countriesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    zones: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    zonesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("postalCodes.title.edit")
        : this.$t("postalCodes.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("postalCodes.datatable.code"),
          value: ENTITY_NAME + "code"
        },
        {
          text: this.$t("postalCodes.datatable.country"),
          value: ENTITY_NAME + "countryCode"
        },
        {
          text: this.$t("postalCodes.datatable.agency"),
          value: ENTITY_NAME + "agencyCode"
        },
        {
          text: this.$t("postalCodes.datatable.zone"),
          value: ENTITY_NAME + "zoneCode"
        },
        {
          text: this.$t("postalCodes.datatable.time"),
          value: ENTITY_NAME + "trafficTime"
        }
      ];
    },
    countriesItems() {
      return this.countries.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    countriesFilterItems() {
      return this.countriesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesFilterItems() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    zonesItems() {
      return this.zones.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    zonesFilterItems() {
      return this.zonesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    isCountrySelected() {
      return (
        this.item.countryCode === undefined || this.item.countryCode === null
      );
    },
    isAgencySelected() {
      return (
        this.item.agencyCode === undefined || this.item.agencyCode === null
      );
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "countries.search": function(value) {
      console.debug("countries.search");
      this.preLoadGenericSearchEntity(value, this.countries, CONTEXT_COUNTRIES);
    },
    "countriesFilter.search": function(value) {
      console.debug("countriesFilter.search");
      this.preLoadGenericSearchEntity(
        value,
        this.countriesFilter,
        CONTEXT_COUNTRIES
      );
    },
    "agencies.search": function(value) {
      console.debug("agencies.search");
      let aditionalFilter = {
        countryCode: this.item.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agencies,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    },
    "agenciesFilter.search": function(value) {
      console.debug("agenciesFilter.search");
      let aditionalFilter = {
        countryCode: this.filter.countryCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES,
        aditionalFilter
      );
    },
    "zones.search": function(value) {
      console.debug("zones.search");
      let aditionalFilter = {
        agencyCode: this.item.agencyCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.zones,
        CONTEXT_ZONES,
        aditionalFilter
      );
    },
    "zonesFilter.search": function(value) {
      console.debug("zonesFilter.search");
      let aditionalFilter = {
        agencyCode: this.filter.agencyCode
      };
      this.preLoadGenericSearchEntity(
        value,
        this.zonesFilter,
        CONTEXT_ZONES,
        aditionalFilter
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.item.agencyCode = this.$userIsAdmin()
        ? ""
        : this.$keycloak.tokenParsed.agency;
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");
      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id, country) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + country + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.countryString = item.country.code + " - " + item.country.name;
      setTimeout(() => {
        this.agencies.search = item.agency.code + " - " + item.agency.name;
        this.zones.search = item.zone.code + " - " + item.zone.name;
      }, 500);
    },
    async update() {
      console.debug("Update form");
      localhttp
        .put(
          CONTEXT + "/update/" + this.item.countryCode + "/" + this.item.code,
          this.item
        )
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id, country) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + country + "/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id, country) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + country + "/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetCountriesSearch();
      this.resetAgenciesSearch();
      this.resetZonesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetCountriesSearch() {
      console.debug("resetCountriesSearch");
      this.countries.search = "";
      this.countries.entries = [];
    },
    resetAgenciesSearch() {
      console.debug("resetAgenciesSearch");
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    resetZonesSearch() {
      console.debug("resetZonesSearch");
      this.zones.search = "";
      this.zones.entries = [];
    },
    filterExport(jsonData) {
      jsonData.forEach(function(v) {
        delete v.country;
        delete v.agency;
        delete v.zone;
      });
    },
    integerOnlyField
  },
  beforeMount() {
    this.filter.agencyCode = this.$userIsAdmin()
      ? ""
      : this.$keycloak.tokenParsed.agency;
  }
};
</script>
